import { defineStore } from 'pinia'
import { supabase } from '@/supabase'
import { useStoreAuth } from '@/stores/storeAuth'

export const useStoreMessages = defineStore('storeMessages', {
  state: () => ({
    conversations: [],
    activeConversation: null,
    activeMessages: [],
    unreadCount: 0,
    isLoading: false,
    hasMore: true,
    conversationsHasMore: true
  }),

  actions: {
    /**
     * Send content (title, review, or list) to one or more users
     * @param {Object} params - Send parameters
     * @param {string[]} params.recipientIds - Array of user IDs to send to
     * @param {number} [params.tmdbId] - TMDB ID of title being shared
     * @param {string} [params.tmdbType] - TMDB type ('movie' or 'tv')
     * @param {string} [params.reviewId] - Review ID being shared
     * @param {string} [params.listId] - List ID being shared
     * @param {string} [params.message] - Optional message to include
     */
    async sendToUsers({
      recipientIds,
      tmdbId,
      tmdbType,
      reviewId,
      listId,
      message
    }) {
      const { data, error } = await supabase.functions.invoke('messages/send', {
        body: {
          recipient_ids: recipientIds,
          tmdb_id: tmdbId,
          tmdb_type: tmdbType,
          review_id: reviewId,
          list_id: listId,
          message
        }
      })

      if (error) {
        console.error('Error sending message:', error)
        throw error
      }

      return data
    },

    /**
     * Fetch user's conversations
     * @param {number} [limit=20] - Number of conversations to fetch
     * @param {number} [offset=0] - Offset for pagination
     */
    async fetchConversations(limit = 20, offset = 0) {
      this.isLoading = true

      try {
        const { data, error } = await supabase.functions.invoke(
          `messages/conversations?limit=${limit}&offset=${offset}`,
          { method: 'GET' }
        )

        if (error) {
          console.error('Error fetching conversations:', error)
          throw error
        }

        if (offset === 0) {
          this.conversations = data.conversations || []
        } else {
          this.conversations = [
            ...this.conversations,
            ...(data.conversations || [])
          ]
        }

        this.conversationsHasMore = data.has_more
        // Authoritative total from the server - the loaded page may be partial
        this.fetchUnreadCount()
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Fetch a single conversation with messages
     * @param {string} conversationId - Conversation ID
     * @param {number} [limit=50] - Number of messages to fetch
     * @param {number} [offset=0] - Offset for pagination
     */
    async fetchConversation(conversationId, limit = 50, offset = 0) {
      this.isLoading = true

      try {
        const { data, error } = await supabase.functions.invoke(
          `messages/conversations/${conversationId}?limit=${limit}&offset=${offset}`,
          { method: 'GET' }
        )

        if (error) {
          console.error('Error fetching conversation:', error)
          throw error
        }

        this.activeConversation = data.conversation
        if (offset === 0) {
          this.activeMessages = data.messages || []
        } else {
          // offset pages back through history; older messages prepend
          this.activeMessages = [
            ...(data.messages || []),
            ...this.activeMessages
          ]
        }
        this.hasMore = data.has_more

        return data
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Reply to a conversation
     * @param {string} conversationId - Conversation ID
     * @param {string} content - Message content
     */
    async reply(conversationId, content) {
      const { data, error } = await supabase.functions.invoke(
        `messages/conversations/${conversationId}/reply`,
        {
          body: { content }
        }
      )

      if (error) {
        console.error('Error sending reply:', error)
        throw error
      }

      // Add message to active messages
      if (this.activeConversation?.id === conversationId) {
        this.activeMessages.push(data.message)
      }

      // Update conversation in list
      this.updateConversationPreview(conversationId, content)

      return data
    },

    /**
     * Add reaction to a conversation
     * @param {string} conversationId - Conversation ID
     * @param {string} [emoji] - Emoji reaction
     * @param {string} [content] - Text reaction/comment
     */
    async react(conversationId, emoji, content) {
      const { data, error } = await supabase.functions.invoke(
        `messages/conversations/${conversationId}/react`,
        {
          body: { emoji, content }
        }
      )

      if (error) {
        console.error('Error adding reaction:', error)
        throw error
      }

      // Add message to active messages if viewing this conversation
      if (this.activeConversation?.id === conversationId) {
        this.activeMessages.push(data.message)
      }

      return data
    },

    /**
     * Mark a conversation as read
     * @param {string} conversationId - Conversation ID
     */
    async markRead(conversationId) {
      const { data, error } = await supabase.functions.invoke(
        `messages/conversations/${conversationId}/read`,
        { method: 'PATCH' }
      )

      if (error) {
        console.error('Error marking as read:', error)
        throw error
      }

      // Update local state
      const conversation = this.conversations.find(
        (c) => c.id === conversationId
      )
      if (conversation) {
        conversation.unread_count = 0
      }

      this.fetchUnreadCount()
      return data
    },

    /**
     * Update conversation preview after sending a message
     */
    updateConversationPreview(conversationId, preview) {
      const conversation = this.conversations.find(
        (c) => c.id === conversationId
      )
      if (conversation) {
        conversation.last_message_preview = preview.substring(0, 100)
        conversation.last_message_at = new Date().toISOString()

        // Move to top of list
        this.conversations = [
          conversation,
          ...this.conversations.filter((c) => c.id !== conversationId)
        ]
      }
    },

    /**
     * Clear active conversation
     */
    clearActiveConversation() {
      this.activeConversation = null
      this.activeMessages = []
      this.hasMore = true
    },

    /**
     * Reset all state
     */
    reset() {
      this.conversations = []
      this.activeConversation = null
      this.activeMessages = []
      this.unreadCount = 0
      this.isLoading = false
      this.hasMore = true
      this.conversationsHasMore = true
    },

    /**
     * Get recommendations for a title (who sent this title to you)
     * @param {string} titleId - The title ID (internal UUID, not TMDB ID)
     * @returns {Promise<Array>} - Array of recommendations with sender info
     */
    async getRecommendationsForTitle(titleId) {
      try {
        const { data, error } = await supabase.functions.invoke(
          `messages/recommendations/title/${titleId}`,
          { method: 'GET' }
        )

        if (error) {
          console.error('Error fetching recommendations:', error)
          return []
        }

        return data?.recommendations || []
      } catch (err) {
        console.error('Error fetching recommendations:', err)
        return []
      }
    },

    /**
     * Fetch unread message count from server
     */
    async fetchUnreadCount() {
      // Only fetch if user is authenticated
      const storeAuth = useStoreAuth()
      if (!storeAuth.user) {
        this.unreadCount = 0
        return 0
      }

      try {
        const { data, error } = await supabase.functions.invoke(
          'messages/unread-count',
          { method: 'GET' }
        )

        if (error) {
          console.error('Error fetching unread count:', error)
          return 0
        }

        this.unreadCount = data?.unread_count || 0
        return this.unreadCount
      } catch (err) {
        console.error('Error fetching unread count:', err)
        return 0
      }
    },

    /**
     * Subscribe to realtime updates for messages
     */
    subscribeToMessages() {
      const channel = supabase
        .channel('messages-realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages'
          },
          (payload) => {
            const newMessage = payload.new
            const storeAuth = useStoreAuth()
            const isOwn = newMessage.sender_id === storeAuth.user?.id
            // Bare emoji reactions surface on the review, not as unread mail
            const isBareReaction =
              !!newMessage.reaction_emoji && !newMessage.content

            // If viewing this conversation, add the message
            if (this.activeConversation?.id === newMessage.conversation_id) {
              // Check if message already exists (we might have added it optimistically)
              const exists = this.activeMessages.some(
                (m) => m.id === newMessage.id
              )
              if (!exists) {
                this.activeMessages.push(newMessage)
              }
            }

            // Update conversation in list
            const conversation = this.conversations.find(
              (c) => c.id === newMessage.conversation_id
            )
            if (conversation) {
              conversation.last_message_preview =
                newMessage.content?.substring(0, 100) ||
                (newMessage.reaction_emoji
                  ? `Reacted with ${newMessage.reaction_emoji}`
                  : '')
              conversation.last_message_at = newMessage.created_at
              if (!isOwn && !isBareReaction) {
                conversation.unread_count = (conversation.unread_count || 0) + 1
              }
            } else if (!isOwn && !isBareReaction) {
              // Message in a conversation we haven't loaded - refresh the list
              this.fetchConversations()
            }

            if (!isOwn && !isBareReaction) {
              this.fetchUnreadCount()
            }
          }
        )
        .subscribe()

      return () => {
        supabase.removeChannel(channel)
      }
    }
  },

  getters: {
    /**
     * Get conversation by ID
     */
    getConversationById: (state) => {
      return (id) => state.conversations.find((c) => c.id === id)
    },

    /**
     * Get conversation by content (title, review, or list)
     */
    getConversationByContent: (state) => {
      return ({ titleId, reviewId, listId }) => {
        return state.conversations.find((c) => {
          if (reviewId && c.review_id === reviewId) return true
          if (listId && c.list_id === listId) return true
          if (titleId && c.title_id === titleId) return true
          return false
        })
      }
    },

    /**
     * Check if there are unread messages
     */
    hasUnread: (state) => state.unreadCount > 0,

    /**
     * Get conversations sorted by most recent
     */
    sortedConversations: (state) => {
      return [...state.conversations].sort((a, b) => {
        const dateA = new Date(a.last_message_at || a.created_at)
        const dateB = new Date(b.last_message_at || b.created_at)
        return dateB - dateA
      })
    }
  }
})
